import {ShoppingOutlined, UserOutlined} from '@ant-design/icons';
import {Alert, Avatar, Button, Col, Form, Input, Row, Select, Spin} from 'antd';
import {useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {
  RQGetProfileAPI,
  RQUpdateUserProfileAPI,
} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

import {OrderSummary} from './OrderSummary';

const SingleUser = () => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const {id} = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {tokenData} = useSelector(selectAuth);

  const {
    data: userDetails,
    isLoading,
    error,
    refetch,
  } = RQGetProfileAPI(id, tokenData);

  const {mutate: updateUser, isLoading: isUpdating} =
    RQUpdateUserProfileAPI({
      onSuccess: () => {
        refetch();
        setIsEditing(false);
        toast.success('User profile updated successfully');
      },
      onError: error => {
        toast.error(error?.message || 'Failed to update');
      },
    });

  const handleEdit = () => setIsEditing(true);

  const INDIA_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
  ];

  const handleCancel = () => {
    form.setFieldsValue(userDetails);
    setIsEditing(false);
  };

  useEffect(() => {
    if (userDetails && !isEditing) {
      form.setFieldsValue(userDetails);
    }
  }, [userDetails, isEditing, form]);

  const onFinish = values => {
    updateUser({
      token: tokenData,
      formData: values,
      id,
    });
  };

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-screen'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={'Error loading user'}
        description={error?.message}
        type={'error'}
      />
    );
  }

  return (
    <div className={'relative min-h-screen bg-gray-50 md:px-4 py-8'}>
      <div className={'max-w-5xl mx-auto flex flex-col lg:flex-row gap-6'}>

        {/* SIDEBAR */}
        <div className={'w-full h-96 lg:w-64 p-6 bg-primary-50 shadow-md rounded-md'}>
          <div className={'flex flex-col items-center gap-3 mb-6'}>
            <Avatar size={80} className={'bg-blue-800 text-white text-2xl'}>
              {`${userDetails?.firstName?.[0] ?? ''}${userDetails?.lastName?.[0] ?? ''}`.toUpperCase()}
            </Avatar>

            <h2 className={'text-xl font-semibold capitalize'}>
              {userDetails?.firstName} {userDetails?.lastName}
            </h2>
            <p className={'text-gray-500 text-sm'}>{userDetails?.email}</p>
          </div>

          <div className={'flex flex-col gap-3 w-full mt-6'}>
            <Button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center justify-center gap-2 ${
                activeTab === 'profile'
                  ? 'bg-blue-800 hover:!bg-primary-900 text-primary-50 hover:!text-primary-50'
                  : 'border border-primary-800 text-primary-950'
              }`}
              block
            >
              <UserOutlined /> User Profile
            </Button>

            <Button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center justify-center gap-2 ${
                activeTab === 'orders'
                  ? 'bg-blue-800 hover:!bg-primary-900 text-primary-50 hover:!text-primary-50'
                  : 'border border-primary-800 text-primary-950'
              }`}
              block
            >
              <ShoppingOutlined /> Order Details
            </Button>
          </div>
        </div>

        {/* CONTENT */}
        <div className={'flex-1 bg-primary-50 rounded-md md:p-6 p-3'}>

          {activeTab === 'profile' && (

            <Form
              form={form}
              layout={'vertical'}
              onFinish={onFinish}
              className={'bg-primary-50 md:p-6'}
            >
              <h1 className={'header'}>User Profile</h1>

              <Row gutter={[16, 8]}>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'First Name'} name={'firstName'} rules={[
                    {required: true},
                    {whitespace: true},
                    {pattern: /^[A-Za-z]+$/, message: 'Only letters are allowed'},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'Last Name'} name={'lastName'} rules={[
                    {required: true},
                    {whitespace: true},
                    {pattern: /^[A-Za-z]+$/, message: 'Only letters are allowed'},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'Email'} name={'email'}>
                    <Input className={'!text-[#525252]'} disabled />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'Mobile'} name={'mobile'} rules={[
                    {required: true, message: 'Enter contact number'},
                    {pattern: /^[0-9]{10}$/, message: 'Contact number must be 10 digits'},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'Street'} name={'street'} rules={[
                    {required: true, message: 'Street is required'},
                    {whitespace: true},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'Apartment'} name={'apartment'} rules={[
                    {required: true, message: 'Apartment is required'},
                    {whitespace: true},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'City'} name={'city'} rules={[
                    {required: true, message: 'City is required'},
                    {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'District'} name={'district'} rules={[
                    {required: true, message: 'District is required'},
                    {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'State'} name={'state'} rules={[
                    {required: true, message: 'State is required'},
                    {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                  ]}>
                    <Select
                      showSearch
                      disabled={!isEditing}  
                      className={'!text-[#525252]'}
                      placeholder={'Select State / UT'}
                      optionFilterProp={'children'}
                      filterOption={(input, option) =>
                        option?.children?.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {INDIA_STATES.map(st => (
                        <Select.Option key={st} value={st}>
                          {st}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item label={'Pincode'} name={'pinCode'} rules={[
                    {required: true, message: 'Pincode is required'},
                    {pattern: /^[1-9][0-9]{5}$/, message: 'Invalid pincode'},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} maxLength={6} />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item label={'Address'} name={'address'} rules={[
                    {required: true, message: 'Address cannot be empty'},
                    {whitespace: true},
                  ]}>
                    <Input className={'!text-[#525252]'} disabled={!isEditing} />
                  </Form.Item>
                </Col>

              </Row>

              <Form.Item className={'mt-4 flex justify-center items-center'}>
                {!isEditing ? (
                  <Button
                    className={'bg-blue-800 hover:!bg-primary-900 text-white'}
                    onClick={handleEdit}
                    block
                  >
            Edit Profile
                  </Button>
                ) : (
                  <div className={'flex gap-3 w-full'}>
                    <Button
                      className={'bg-blue-800 hover:!bg-primary-900 hover:!text-white text-white'}
                      htmlType={'submit'}
                      loading={isUpdating}
                      block
                    >
              Save Changes
                    </Button>

                    <Button
                      className={'border border-primary-800 text-primary-900'}
                      onClick={handleCancel}
                      block
                    >
              Cancel
                    </Button>
                  </div>
                )}
              </Form.Item>

            </Form>
          )}

          {activeTab === 'orders' && (
            <OrderSummary userId={id} />
          )}

        </div>
      </div>
    </div>
  );
};

export default SingleUser;
